import React, { useState } from 'react';
import ProjectTaskMedia from './ProjectTaskMedia';
import { addTaskVendor, removeTaskVendor, taskVendorList } from '../shared/projectTaskVendor';
import { MAX_TASK_PHOTOS, VENDOR_LIMIT_MESSAGE } from '../shared/projectTaskLimits';

function newTask() {
  return {
    id: crypto.randomUUID(),
    title: '',
    vendors: [],
    photos: [],
  };
}

/**
 * A homeowner starts a project, adds tasks, and can attach a photo
 * before the project is saved. The photo uploads after the row exists.
 */
function HomeownerProjectForm({ colors, brandColor, vendorOptions, busy, onCancel, onSubmit }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [tasks, setTasks] = useState([newTask()]);
  const [notice, setNotice] = useState('');

  const field = {
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '44px',
    marginTop: '0.35rem',
    background: colors.surface,
    color: colors.text,
    border: `2px solid ${colors.border}`,
    borderRadius: '0.5rem',
    fontSize: '1rem',
    fontFamily: 'inherit',
  };

  const updateTask = (id, next) => {
    setTasks((current) => current.map((task) => (task.id === id ? next : task)));
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        setNotice('');
        const named = tasks.filter((task) => task.title.trim());
        if (!title.trim() || named.length === 0) {
          setNotice('Add a title and at least one task.');
          return;
        }
        onSubmit({
          title: title.trim(),
          description: description.trim(),
          due_date: dueDate,
          tasks: named.map((task) => ({
            id: task.id,
            title: task.title.trim(),
            vendors: taskVendorList(task, (vendorOptions || []).map((option) => option.vendor).filter(Boolean)),
            photos: (task.photos || []).map((photo) => photo.file).filter(Boolean),
          })),
        });
      }}
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: '0.75rem',
        padding: '1rem',
        marginBottom: '1.5rem',
      }}
    >
      <h3 style={{ margin: '0 0 0.75rem', color: colors.text, fontWeight: 650 }}>New project</h3>
      <label style={{ display: 'block', color: colors.text, fontWeight: 650 }}>
        Title
        <input value={title} onChange={(event) => setTitle(event.target.value)} required style={field} />
      </label>
      <label style={{ display: 'block', color: colors.text, fontWeight: 650, marginTop: '0.75rem' }}>
        Description
        <textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={2} style={field} />
      </label>
      <label style={{ display: 'block', color: colors.text, fontWeight: 650, marginTop: '0.75rem' }}>
        Due date
        <input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} style={field} />
      </label>

      <div style={{ marginTop: '1rem' }}>
        {tasks.map((task, index) => (
          <div key={task.id} style={{ marginBottom: '0.75rem', padding: '0.75rem', background: colors.surfaceAlt, borderRadius: '0.5rem' }}>
            <label style={{ display: 'block', color: colors.text, fontWeight: 650 }}>
              {`Task ${index + 1}`}
              <input
                aria-label={`Task ${index + 1} title`}
                value={task.title}
                onChange={(event) => updateTask(task.id, { ...task, title: event.target.value })}
                style={field}
              />
            </label>
            <ProjectTaskMedia
              colors={colors}
              brandColor={brandColor}
              vendors={taskVendorList(task)}
              photos={(task.photos || []).map((photo) => ({ id: photo.id, url: photo.preview, removable: true }))}
              vendorOptions={vendorOptions}
              canEditVendors
              canAddPhoto={(task.photos || []).length < MAX_TASK_PHOTOS}
              onAddVendor={(value) => {
                const option = (vendorOptions || []).find((item) => item.value === value);
                const loaded = (vendorOptions || []).map((item) => item.vendor).filter(Boolean);
                const added = addTaskVendor(task, option && option.vendor, loaded);
                if (added.error) setNotice(added.error || VENDOR_LIMIT_MESSAGE);
                else updateTask(task.id, added.task);
              }}
              onRemoveVendor={(vendor) => {
                const loaded = (vendorOptions || []).map((item) => item.vendor).filter(Boolean);
                updateTask(task.id, removeTaskVendor(task, vendor.source, vendor.id, loaded));
              }}
              onPhotoFile={(fileOrFiles, error) => {
                if (error) {
                  setNotice(error.message || 'Could not use that photo.');
                  return;
                }
                const incoming = (Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles]).filter(Boolean);
                if (!incoming.length) return;
                const existing = task.photos || [];
                const room = MAX_TASK_PHOTOS - existing.length;
                const next = incoming.slice(0, room).map((file) => ({
                  id: crypto.randomUUID(),
                  file,
                  preview: URL.createObjectURL(file),
                }));
                updateTask(task.id, { ...task, photos: existing.concat(next) });
              }}
              onRemovePhoto={(photo) => {
                const current = (task.photos || []).find((item) => item.id === photo.id);
                if (current && current.preview) URL.revokeObjectURL(current.preview);
                updateTask(task.id, {
                  ...task,
                  photos: (task.photos || []).filter((item) => item.id !== photo.id),
                });
              }}
            />
          </div>
        ))}
        <button
          type="button"
          onClick={() => setTasks((current) => current.concat([newTask()]))}
          style={{
            background: 'transparent',
            border: 'none',
            color: brandColor,
            fontWeight: 650,
            cursor: 'pointer',
            minHeight: '44px',
          }}
        >
          Add task
        </button>
      </div>

      {notice ? <p style={{ color: '#b45309', margin: '0.5rem 0' }}>{notice}</p> : null}

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
        <button
          type="submit"
          disabled={busy}
          style={{
            minHeight: '44px',
            padding: '0.6rem 1rem',
            border: 'none',
            borderRadius: '0.75rem',
            background: brandColor,
            color: '#fff',
            fontWeight: 650,
            cursor: 'pointer',
          }}
        >
          {busy ? 'Saving…' : 'Create project'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{
            minHeight: '44px',
            padding: '0.6rem 1rem',
            borderRadius: '0.75rem',
            border: `1px solid ${colors.border}`,
            background: colors.surface,
            color: colors.text,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default HomeownerProjectForm;
